import { useEffect } from 'react';

import { PartnerProfile } from 'pages/partners/components/PartnerProfile/PartnerProfile';
import { Partner, PartnerApi, partnerList, PartnerTypes } from 'apis/PartnerApi';
import { IntlProvider, LocalizationProvider } from '@progress/kendo-react-intl';
import { PartnerDetailsContext, usePartners } from 'pages/partners/context';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { useNotification } from 'app/notifications';
import { PageWithHeader } from 'components/layout';
import { helpContent } from 'pages/partners/help';
import { KendoIntlProvider } from 'app/culture';
import { useContext } from 'components/Tabs';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';

import 'pages/partners/styles/main.scss';
import 'styles/global-styles.scss';

export const PartnerDetail = () => {

    const { id } = useParams();
    const partnerId = Number(id);
    const navigate = useNavigate();
    const { search } = useLocation();

    const [state, setState] = useSuperState({
        loading: true
    });

    const { open, pageContext } = useContext(PartnerDetailsContext);
    const { partner } = usePartners();
    const { formatMessage, locale } = useIntl();
    const { createToast } = useNotification();

    const urlPartnerType = new URLSearchParams(search).get('PType') ?? new URLSearchParams(search).get('ptype');
    const parsedUrlPartnerType = urlPartnerType ? Number(urlPartnerType) : null;
    const activePartnerType = (
        (Number.isFinite(parsedUrlPartnerType) ? parsedUrlPartnerType : partner.type) as PartnerTypes | null
    );

    const loadPartner = async () => {

        try {

            setState({ loading: true });
            const partnerDetails = await PartnerApi.Get(partnerId);
            open({
                Data: {
                    originalPartnerDetails: partnerDetails,
                    partnerDetails
                },
                HasChanges: false,
                Id: partnerDetails.ID,
                Page: 'Partners',
                Title: partnerDetails.Name
            });

        } catch (e) {

            console.error(e);
            createToast(formatMessage({ id: 'failed_to_load_partner_details_please_tr.5fd2bbee', defaultMessage: 'Failed to load partner details. Please try again.' }), 'danger');

        } finally {

            setState({ loading: false });

        }

    };

    // Re-fetch and re-open the partner (used after an edit, or to jump to another partner).
    const onDetailsItemClick = async (dataItem: Partner) => {

        const partnerDetails = await PartnerApi.Get(dataItem.ID);
        navigate('/partners/' + dataItem.ID);
        open({
            Data: {
                originalPartnerDetails: partnerDetails,
                partnerDetails
            },
            HasChanges: false,
            Id: dataItem.ID,
            Page: 'Partners',
            Title: dataItem.Name
        });

    };

    const getPartnerTypeName = () => {

        const detailType = (pageContext?.Data?.partnerDetails?.PartnerType as PartnerTypes | undefined) ?? activePartnerType;
        const indexOf = partnerList.findIndex(partnerItem => partnerItem.Id === detailType);
        return indexOf !== -1 ? `${partnerList[indexOf].Name}` : formatMessage({ id: 'partner.cb022868', defaultMessage: 'Partner' });

    };

    const getPartnerTypeBreadcrumbLabel = () => {

        const resolvedType = (pageContext?.Data?.partnerDetails?.PartnerType as PartnerTypes | undefined) ?? activePartnerType;

        switch (resolvedType) {

            case PartnerTypes.TestingCenter:
                return 'Testing Centers';
            case PartnerTypes.Distributor:
                return 'Distributors';
            case PartnerTypes.Reseller:
                return 'Resellers';
            case PartnerTypes.Association:
                return 'Associations';
            default:
                return formatMessage({ id: 'partner.cb022868', defaultMessage: 'Partner' });

        }

    };

    const getPartnerTypeBreadcrumbUrl = () => {

        const resolvedType = (pageContext?.Data?.partnerDetails?.PartnerType as PartnerTypes | undefined) ?? activePartnerType;
        return resolvedType ? `/partners?PType=${resolvedType}` : '/partners';

    };

    useEffect(() => {

        loadPartner();

    }, [partnerId]);

    return (
        <KendoIntlProvider>
            <LocalizationProvider language={locale}>
                <IntlProvider locale={locale}>
                    <div className="partner-detail d-flex flex-column gap-3">
                        <PageWithHeader
                            headerConfig={{
                                helpContent: helpContent,
                                title: pageContext?.Title ?? '',
                                trail: [{ label: getPartnerTypeBreadcrumbLabel(), to: getPartnerTypeBreadcrumbUrl() }]
                            }}
                        >

                            {state.loading || !pageContext?.Data?.partnerDetails ?
                                (
                                    <div className="d-flex align-items-center justify-content-center">
                                        <GMetrixLoader />
                                    </div>
                                )
                                :
                                (
                                    <PartnerProfile
                                        details={pageContext.Data.partnerDetails}
                                        getPartnerTypeName={getPartnerTypeName}
                                        onDetailsItemClick={onDetailsItemClick}
                                        {...pageContext}
                                    />
                                )
                            }

                        </PageWithHeader>
                    </div>
                </IntlProvider>
            </LocalizationProvider>
        </KendoIntlProvider>
    );

};
